import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadApiError} from '../zad/errors'
import {
  createZadProductType,
  fetchZadProductTypeById,
  fetchZadProductTypeOptions,
  fetchZadProductTypes,
  updateZadProductType,
  updateZadProductTypeStatus,
} from './service'
import {FetchZadProductTypesResponse, ZadProductTypeState} from './types'

const initialState: ZadProductTypeState = {
  productTypes: [],
  selectProductTypes: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  selectLoading: false,
  actionLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadProductTypeState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadProductTypeState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

const zadProductTypeSlice = createSlice({
  name: 'zadProductTypes',
  initialState,
  reducers: {
    clearZadProductTypeError: (state: ZadProductTypeState) => {
      clearError(state)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadProductTypes.pending, (state: ZadProductTypeState) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(
      fetchZadProductTypes.rejected,
      (state: ZadProductTypeState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.listLoading = false
        state.listInitialized = true
      }
    )
    builder.addCase(
      fetchZadProductTypes.fulfilled,
      (state: ZadProductTypeState, action: PayloadAction<FetchZadProductTypesResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.productTypes = action.payload.productTypes
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadProductTypeOptions.pending, (state: ZadProductTypeState) => {
      state.selectLoading = true
    })
    builder.addCase(
      fetchZadProductTypeOptions.rejected,
      (state: ZadProductTypeState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.selectLoading = false
      }
    )
    builder.addCase(fetchZadProductTypeOptions.fulfilled, (state: ZadProductTypeState, action) => {
      state.selectLoading = false
      state.selectProductTypes = action.payload.productTypes
    })

    builder.addCase(fetchZadProductTypeById.pending, (state: ZadProductTypeState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      fetchZadProductTypeById.rejected,
      (state: ZadProductTypeState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(fetchZadProductTypeById.fulfilled, (state: ZadProductTypeState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(createZadProductType.pending, (state: ZadProductTypeState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      createZadProductType.rejected,
      (state: ZadProductTypeState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(createZadProductType.fulfilled, (state: ZadProductTypeState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadProductType.pending, (state: ZadProductTypeState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      updateZadProductType.rejected,
      (state: ZadProductTypeState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(updateZadProductType.fulfilled, (state: ZadProductTypeState) => {
      state.actionLoading = false
      clearError(state)
    })

    builder.addCase(updateZadProductTypeStatus.pending, (state: ZadProductTypeState) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(
      updateZadProductTypeStatus.rejected,
      (state: ZadProductTypeState, action: PayloadAction<any>) => {
        applyRejected(state, action)
        state.actionLoading = false
      }
    )
    builder.addCase(updateZadProductTypeStatus.fulfilled, (state: ZadProductTypeState) => {
      state.actionLoading = false
      clearError(state)
    })
  },
})

export default zadProductTypeSlice.reducer
export const actions = zadProductTypeSlice.actions
export {initialState as zadProductTypeInitialState}
